// calls/index.js
import { io } from 'socket.io-client';
import CallService from './CallService';
import { webMedia } from './media/webMedia';
import { callActionsInternal } from './storeGlue';
import { socketPath, wsApi } from '../urlConfig';

const CALL_DEVICE_STORAGE_KEY = 'alotb.calls.deviceId.v2';
const CALL_CLIENT_TYPE = 'front';

const getToken = () => window.localStorage.getItem('token') || null;

const createId = () => {
    try {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    } catch (_error) { }

    try {
        const bytes = new Uint8Array(16);
        window.crypto?.getRandomValues?.(bytes);
        const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
        if (hex) return `web-${hex}`;
    } catch (_error) { }

    return `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
};

const getDeviceId = () => {
    try {
        const stored = window.sessionStorage.getItem(CALL_DEVICE_STORAGE_KEY);
        if (stored) return stored;

        const created = createId();
        window.sessionStorage.setItem(CALL_DEVICE_STORAGE_KEY, created);
        return created;
    } catch (_error) {
        return createId();
    }
};

const getStoredUserId = () => {
    try {
        const raw = window.localStorage.getItem('user');
        if (!raw) return null;
        const user = JSON.parse(raw);
        return user?._id || user?.id || null;
    } catch (_error) {
        return null;
    }
};

const syncMeId = () => {
    const id = getStoredUserId();
    if (id) window.__ME_ID__ = String(id);
    return id;
};

const deviceId = getDeviceId();
syncMeId();

const callsUrl = `${wsApi}/calls`;
const callsSocketConfigKey = `${callsUrl}|${socketPath}|${CALL_CLIENT_TYPE}|${deviceId}`;

const existingSocket = window.__CALLS_SOCKET__;
const existingConfigKey = window.__CALLS_SOCKET_CONFIG_KEY__;

if (existingSocket && existingConfigKey !== callsSocketConfigKey) {
    existingSocket.disconnect();
    delete window.__CALLS_SOCKET__;
    delete window.__CALL_SERVICE__;
    delete window.__CALLS_SOCKET_CONFIG_KEY__;
    delete window.__CALL_GLUE_BOUND__;
    delete window.__CALL_SOCKET_EVENTS_BOUND__;
}

const buildAuth = () => ({
    token: getToken() || undefined,
    deviceId,
    clientType: CALL_CLIENT_TYPE,
});

export const callsSocket =
    window.__CALLS_SOCKET__ ||
    io(callsUrl, {
        transports: ['websocket', 'polling'],
        path: socketPath,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 800,
        reconnectionDelayMax: 5000,
        timeout: 15000,
        withCredentials: false,
        autoConnect: Boolean(getToken()),
        auth: buildAuth(),
    });

window.__CALLS_SOCKET__ = callsSocket;
window.__CALLS_SOCKET_CONFIG_KEY__ = callsSocketConfigKey;
window.__CALL_DEVICE_ID__ = deviceId;
window.__CALL_CLIENT_TYPE__ = CALL_CLIENT_TYPE;

const defaultIce = [{ urls: 'stun:stun.l.google.com:19302' }];

export const callService =
    window.__CALL_SERVICE__ ||
    new CallService({
        socket: callsSocket,
        media: webMedia,
        iceServers: defaultIce,
    });

window.__CALL_SERVICE__ = callService;

if (!window.__CALL_SOCKET_EVENTS_BOUND__) {
    window.__CALL_SOCKET_EVENTS_BOUND__ = true;

    callsSocket.on('connect', () => {
        syncMeId();
        const auth = buildAuth();
        callsSocket.auth = auth;

        if (auth.token) {
            callsSocket.emit('auth:token', auth);
        }

        callService.resumeSignaling();

        console.log('[calls] connected', {
            id: callsSocket.id,
            url: callsUrl,
            path: socketPath,
            me: window.__ME_ID__ || null,
            deviceId,
            clientType: CALL_CLIENT_TYPE,
        });
    });

    callsSocket.on('connect_error', (error) => {
        console.warn('[calls] connect_error', error?.message || error);
    });

    callsSocket.on('disconnect', (reason) => {
        console.log('[calls] disconnected', reason);
    });
}

export function bindCallListeners(dispatch, getState) {
    callActionsInternal(callService, dispatch, getState);
}

export function refreshCallsAuth() {
    syncMeId();

    const auth = buildAuth();
    callsSocket.auth = auth;

    if (!auth.token) {
        if (callsSocket.connected || callsSocket.active) {
            callsSocket.disconnect();
        }
        return;
    }

    if (callsSocket.connected) {
        callsSocket.emit('auth:token', auth);
        callService.resumeSignaling();
        return;
    }

    callsSocket.connect();
}

try {
    Object.defineProperty(window, '__pc', {
        configurable: true,
        get() {
            return callService?.pc || null;
        },
    });
} catch (_error) { }
